import React, { useState, useMemo } from 'react';
import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectBudget({ project, updateProject }) {
  const [newItem, setNewItem] = useState({ category: 'Venue', name: '', estimated: '', actual: '', paid: '' });
  const [isAiGenerating, setIsAiGenerating] = useState(false);

  const budgetItems = project.budgetItems || [];
  const categories = ['Venue', 'Catering', 'Photography', 'Videography', 'Florals', 'Music/DJ', 'Rentals', 'Attire', 'Hair & Makeup', 'Stationery', 'Favors', 'Other'];

  const totals = budgetItems.reduce((acc, item) => ({
    estimated: acc.estimated + (Number(item.estimated) || 0),
    actual: acc.actual + (Number(item.actual) || 0),
    paid: acc.paid + (Number(item.paid) || 0)
  }), { estimated: 0, actual: 0, paid: 0 });

  const balance = totals.actual - totals.paid;

  const groupedItems = categories.reduce((acc, cat) => {
    const catItems = budgetItems.filter(i => i.category === cat);
    if(catItems.length > 0) acc[cat] = catItems;
    return acc;
  }, {});
  
  const otherItems = budgetItems.filter(i => !categories.includes(i.category));
  if(otherItems.length > 0) groupedItems['Other'] = (groupedItems['Other'] || []).concat(otherItems);

  // NEW: AI Budget Allocator
  const handleGenerateAiBudget = async () => {
      if (!project.budgetGoal || project.budgetGoal <= 0) {
          alert("Please set a Total Budget Goal in the Overview tab first!");
          return;
      }
      if (budgetItems.length > 0 && !window.confirm("This will add AI-generated items to your existing budget. Continue?")) return;
      
      setIsAiGenerating(true);
      try {
          const prompt = `You are an expert event planner. The user has a total budget of $${project.budgetGoal}. Generate a realistic, itemized budget breakdown. Return strictly a JSON array of objects with keys: "category" (e.g., Venue, Catering, Photography, Florals, Music/DJ, Rentals), "name" (specific item like "Venue Rental"), and "estimated" (number, the allocated amount). The total estimated must equal roughly ${project.budgetGoal}. Do not include markdown.`;

          const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }], generationConfig: { responseMimeType: "application/json" } };
          const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
           const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
          
          const result = await response.json();
          if (result.candidates?.[0]) {
              const generatedItems = JSON.parse(result.candidates[0].content.parts[0].text);
              const newItems = generatedItems.map((item, i) => ({
                  id: Date.now().toString() + i,
                  category: item.category || 'Other',
                  name: item.name,
                  estimated: Number(item.estimated) || 0,
                  actual: 0,
                  paid: 0
              }));
              updateProject(project.id, { budgetItems: [...budgetItems, ...newItems] });
          } else {
              throw new Error("Invalid AI response");
          }
      } catch (err) {
          console.error("AI Budget Error:", err);
          alert("Failed to generate budget breakdown. Please try again.");
      } finally {
          setIsAiGenerating(false);
      }
  };

  const handlePrintBudget = () => {
      const printWindow = window.open('', '_blank');
      
      const rowsHtml = Object.entries(groupedItems).map(([cat, items]) => `
          <tr style="background-color: #fdf2f8;">
              <td colspan="4" style="padding: 10px; font-weight: bold; color: #be185d;">${cat}</td>
          </tr>
          ${items.map(item => `
              <tr>
                  <td style="padding: 8px 10px; border-bottom: 1px solid #eee;">${item.name}</td>
                  <td style="padding: 8px 10px; border-bottom: 1px solid #eee; text-align: right;">$${(Number(item.estimated)||0).toFixed(2)}</td>
                  <td style="padding: 8px 10px; border-bottom: 1px solid #eee; text-align: right;">$${(Number(item.actual)||0).toFixed(2)}</td>
                  <td style="padding: 8px 10px; border-bottom: 1px solid #eee; text-align: right;">$${(Number(item.paid)||0).toFixed(2)}</td>
              </tr>
          `).join('')}
      `).join('');

      printWindow.document.write(`
          <html>
              <head>
                  <title>Budget Report - ${project.clientNames}</title>
                  <style>
                      body { font-family: 'Inter', sans-serif; padding: 40px; color: #333; max-width: 800px; margin: 0 auto; -webkit-print-color-adjust: exact; }
                      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                      th { background: #f3f4f6; padding: 12px 10px; text-align: left; font-size: 14px; text-transform: uppercase; color: #6b7280; }
                      .summary { display: flex; justify-content: space-between; background: #f9fafb; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
                      .summary-box { text-align: center; }
                      .summary-label { font-size: 12px; color: #6b7280; text-transform: uppercase; font-weight: bold; letter-spacing: 1px; }
                      .summary-val { font-size: 24px; font-weight: bold; color: #111827; margin-top: 5px; }
                      @media print { .no-print { display: none; } body { padding: 0; } }
                  </style>
              </head>
              <body>
                  <h2>Budget Report: ${project.clientNames}</h2>
                  <div class="summary">
                      <div class="summary-box"><div class="summary-label">Total Budget</div><div class="summary-val">$${(project.budgetGoal||0).toLocaleString()}</div></div>
                      <div class="summary-box"><div class="summary-label">Estimated</div><div class="summary-val">$${totals.estimated.toLocaleString()}</div></div>
                      <div class="summary-box"><div class="summary-label">Actual Spent</div><div class="summary-val" style="color: ${totals.actual > project.budgetGoal ? '#dc2626' : '#16a34a'}">$${totals.actual.toLocaleString()}</div></div>
                      <div class="summary-box"><div class="summary-label">Remaining Balance</div><div class="summary-val">$${balance.toLocaleString()}</div></div>
                  </div>
                  <table>
                      <thead><tr><th>Expense Item</th><th style="text-align:right">Estimated</th><th style="text-align:right">Actual</th><th style="text-align:right">Paid</th></tr></thead>
                      <tbody>${rowsHtml}</tbody>
                  </table>
                  <div class="no-print" style="text-align: center; margin-top: 40px;">
                      <button onclick="window.print()" style="padding: 12px 24px; background: #111827; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: bold;">Print / Save PDF</button>
                  </div>
              </body>
          </html>
      `);
      printWindow.document.close();
  };

  const handleExportCSV = () => {
      if (budgetItems.length === 0) {
          alert("No budget items to export.");
          return;
      }
      const headers = "Item Name,Category,Estimated,Actual,Paid\n";
      const csvContent = headers + budgetItems.map(item => {
          return `"${(item.name || '').replace(/"/g, '""')}","${(item.category || '').replace(/"/g, '""')}","${item.estimated || 0}","${item.actual || 0}","${item.paid || 0}"`;
      }).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", `Budget_Expenses_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const addItem = (e) => {
      e.preventDefault();
      if (!newItem.name) return;
      updateProject(project.id, { budgetItems: [...budgetItems, { id: Date.now().toString(), ...newItem, estimated: Number(newItem.estimated) || 0, actual: Number(newItem.actual) || 0, paid: Number(newItem.paid) || 0 }] });
      setNewItem({ category: 'Venue', name: '', estimated: '', actual: '', paid: '' });
  };

  const updateItem = (id, field, value) => {
      const updated = budgetItems.map(i => i.id === id ? { ...i, [field]: value } : i);
      updateProject(project.id, { budgetItems: updated });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm min-w-0">
          <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 truncate">Estimated</p>
          <p className="text-lg md:text-xl font-bold text-gray-900 truncate" title={`$${totals.estimated.toLocaleString()}`}>${totals.estimated.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm border-l-4 border-l-pink-500 min-w-0">
          <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 truncate">Actual</p>
          <p className="text-lg md:text-xl font-bold text-gray-900 truncate" title={`$${totals.actual.toLocaleString()}`}>${totals.actual.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm border-l-4 border-l-emerald-500 min-w-0">
          <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 truncate">Paid</p>
          <p className="text-lg md:text-xl font-bold text-gray-900 truncate" title={`$${totals.paid.toLocaleString()}`}>${totals.paid.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm border-l-4 border-l-amber-500 min-w-0">
          <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 truncate">Balance</p>
          <p className="text-lg md:text-xl font-bold text-gray-900 truncate" title={`$${balance.toLocaleString()}`}>${balance.toLocaleString()}</p>
        </div>
      </div>

      {/* NEW: AI Auto-Allocate Button */}
      <div className="flex justify-end gap-2 mb-2">
          <button onClick={handleExportCSV} className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm flex items-center gap-2">
              ⬇️ Export CSV
          </button>
          <button onClick={handlePrintBudget} className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm flex items-center gap-2">
              🖨️ Print Budget
          </button>
          <button onClick={handleGenerateAiBudget} disabled={isAiGenerating} className="bg-purple-100 text-purple-700 hover:bg-purple-200 px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm flex items-center gap-2">
              {isAiGenerating ? '✨ Generating Breakdown...' : '✨ Auto-Allocate Budget via AI'}
          </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <form onSubmit={addItem} className="bg-gray-50 p-4 border-b border-gray-100 flex flex-col md:flex-row gap-2 items-end">
            <div className="flex-grow w-full">
                <label className="text-xs font-bold text-gray-500 ml-1">Item Name</label>
                <input type="text" placeholder="e.g. Venue Rental" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} className="w-full p-2 border rounded-md" required />
            </div>
            <div className="w-full md:w-32">
                <label className="text-xs font-bold text-gray-500 ml-1">Category</label>
                <select value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} className="w-full p-2 border rounded-md bg-white">
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>
            <div className="w-full md:w-24">
                <label className="text-xs font-bold text-gray-500 ml-1">Est. ($)</label>
                <input type="number" placeholder="0.00" value={newItem.estimated} onChange={e => setNewItem({...newItem, estimated: e.target.value})} className="w-full p-2 border rounded-md" />
            </div>
            <div className="w-full md:w-24">
                <label className="text-xs font-bold text-gray-500 ml-1">Actual ($)</label>
                <input type="number" placeholder="0.00" value={newItem.actual} onChange={e => setNewItem({...newItem, actual: e.target.value})} className="w-full p-2 border rounded-md" />
            </div>
            <button type="submit" className="bg-pink-500 text-white px-4 py-2 rounded-md font-bold hover:bg-pink-600 h-[42px] w-full md:w-auto">Add</button>
        </form>

        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase font-bold text-gray-400 tracking-wider">
                    <tr>
                        <th className="px-4 py-4">Expense Item</th>
                        <th className="px-4 py-4">Category</th>
                        <th className="px-4 py-4 text-right">Estimated</th>
                        <th className="px-4 py-4 text-right">Actual</th>
                        <th className="px-4 py-4 text-center">Paid</th>
                        <th className="px-4 py-4"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {budgetItems.map(item => (
                        <tr key={item.id} className="hover:bg-gray-50 transition">
                            <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
                            <td className="px-4 py-3 text-gray-500">{item.category}</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-600">
                                <input type="number" value={item.estimated || ''} onChange={e => updateItem(item.id, 'estimated', e.target.value)} className="w-20 text-right bg-transparent border-b border-transparent hover:border-gray-300 focus:border-pink-500 outline-none p-1" />
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-gray-900">
                                <input type="number" value={item.actual || ''} onChange={e => updateItem(item.id, 'actual', e.target.value)} className="w-20 text-right bg-transparent border-b border-transparent hover:border-gray-300 focus:border-pink-500 outline-none p-1" />
                            </td>
                            <td className="px-4 py-3 text-center">
                                <input type="checkbox" checked={item.paid} onChange={e => updateItem(item.id, 'paid', e.target.checked)} className="w-4 h-4 text-pink-600 rounded border-gray-300 focus:ring-pink-500 cursor-pointer" />
                            </td>
                            <td className="px-4 py-3 text-right">
                                <button onClick={() => updateProject(project.id, { budgetItems: budgetItems.filter(i => i.id !== item.id) })} className="text-gray-400 hover:text-red-600 font-bold p-1.5 rounded-full hover:bg-red-50 transition">✕</button>
                            </td>
                        </tr>
                    ))}
                    {budgetItems.length === 0 && (
                        <tr><td colSpan="6" className="p-8 text-center text-gray-400 italic">No budget items added. Start planning your expenses above!</td></tr>
                    )}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                    <tr>
                        <td colSpan="2" className="px-4 py-4 text-right font-bold text-gray-700 uppercase tracking-wider text-xs">Totals</td>
                        <td className="px-4 py-4 text-right font-bold text-gray-900">${totals.estimated.toFixed(2)}</td>
                        <td className={`px-4 py-4 text-right font-bold ${totals.actual > project.budgetGoal ? 'text-red-600' : 'text-gray-900'}`}>${totals.actual.toFixed(2)}</td>
                        <td className="px-4 py-4 text-center font-bold text-emerald-600"></td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>
      </div>
    </div>
  );
}
